import { useMemberSubscription } from "@gymflow/api";
import {
  DATE_FORMAT,
  ORDINAL_DATE_FORMAT,
  PARAMETER_DATE_ONLY_FORMAT,
} from "@gymflow/common";
import { cn, currencies, utcToZonedTime } from "@gymflow/helpers";
import { UserMemberSubscriptionBeanWithMembership } from "@gymflow/types";
import { calculateIncTaxPrice } from "apps/portal/src/helpers";
import { ModalContext, useClubSettings } from "apps/portal/src/providers";
import useGymflowModels from "apps/portal/src/store";
import { FormikProvider, useFormik } from "formik";
import moment, { Moment } from "moment-timezone";
import React, { useCallback, useContext, useState } from "react";
import Datetime from "react-datetime";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { ApiResolverContext } from "../../../../providers/ApiResolverProvider/ApiResolverContext";
import { FormPriceBlock } from "../../../atoms";
import { ConfirmModal } from "../../../templates";
import { NotPrimaryMembershipWarning } from "../NotPrimaryMembershipWarning";

export interface UpdateBillingMembershipModalProps {
  memberId: string;
  membership: UserMemberSubscriptionBeanWithMembership;
}

export const UpdateBillingMembershipModal: React.FC<
  UpdateBillingMembershipModalProps
> = ({ memberId, membership }) => {
  const { club } = useClubSettings();
  const { clubId } = useContext(ApiResolverContext);
  const { api } = useGymflowModels();

  const { changeBillingDateMutation, changeSubscriptionPriceMutation } =
    useMemberSubscription({ api, tz: club.timezone });
  const { hide } = useContext(ModalContext);
  const [billingDate, setBillingDate] = useState<string>(
    moment(utcToZonedTime(membership.endDate, club.timezone))
      .add(1, "second")
      .format(DATE_FORMAT),
  );

  const isValidDate = useCallback(
    (date: Moment) => date.isAfter(moment().tz(club.timezone)),
    [club.timezone],
  );

  const formik = useFormik<{
    newSubscriptionPrice: {
      exTaxPrice: number;
      incTaxPrice: number;
      salesTaxRate: number;
    };
  }>({
    initialValues: {
      newSubscriptionPrice: {
        exTaxPrice: calculateIncTaxPrice(
          membership.price,
          membership.membershipBean.taxRate,
        ),
        incTaxPrice: membership.price,
        salesTaxRate: membership.membershipBean.taxRate,
      },
    },
    validationSchema: toFormikValidationSchema(
      z.object({
        newSubscriptionPrice: z.object({
          exTaxPrice: z.number().min(0),
          incTaxPrice: z.number().min(0),
          salesTaxRate: z.number().min(0).max(100),
        }),
      }),
    ),
    onSubmit: () => {},
  });
  return (
    <ConfirmModal
      isConfirmDisabled={!formik.isValid}
      onConfirm={async () => {
        const newSubscriptionPrice =
          formik.values.newSubscriptionPrice.incTaxPrice;

        if (newSubscriptionPrice === undefined) {
          return;
        }

        if (membership.price !== newSubscriptionPrice) {
          await changeSubscriptionPriceMutation.mutateAsync({
            subscriptionId: membership.id,
            memberId: memberId,
            clubId: clubId,
            newSubscriptionPrice,
          });
        }
        await changeBillingDateMutation.mutateAsync({
          clubId,
          subscriptionId: membership.id,
          newDate: moment(billingDate, DATE_FORMAT).format(
            PARAMETER_DATE_ONLY_FORMAT,
          ),
        });
        hide();
      }}
      title="Update Billing"
      onCancel={() => {
        hide();
      }}
    >
      <div className="flex flex-col gap-4">
        <div>
          Change the regular billing date or price. Changes are immediately and
          reflected on the next bill.
        </div>
        <FormikProvider value={formik}>
          <FormPriceBlock
            name="newSubscriptionPrice"
            customLabel={`Membership Price (${currencies[club.defaultCurrency].symbol})`}
            disabledTaxRate
          />
        </FormikProvider>
        <div className="flex flex-col gap-y-1">
          <div className="text-sm font-semibold">Billing Date</div>
          <div
            className={cn(
              "relative flex h-11 w-full cursor-pointer flex-row items-center rounded-lg border border-gray-300 bg-gray-0 text-center text-gray-600 hover:bg-gray-100",
            )}
          >
            <Datetime
              renderInput={(props) => (
                <div
                  {...props}
                  className={cn(
                    props?.className,
                    "flex h-[2.625rem] items-center",
                  )}
                >
                  {moment(billingDate, DATE_FORMAT).format(ORDINAL_DATE_FORMAT)}
                </div>
              )}
              dateFormat={DATE_FORMAT}
              timeFormat={false}
              onChange={(v: Moment | string) => {
                if (typeof v !== "string") {
                  setBillingDate(v.format(DATE_FORMAT));
                }
              }}
              value={billingDate}
              closeOnSelect
              isValidDate={isValidDate}
              renderDay={(props, currentDate: Moment, selectedDate: Moment) => {
                return (
                  <td
                    {...props}
                    className={cn(props?.className, "relative", {
                      "opacity-20": !isValidDate(currentDate),
                      "!bg-secondary-500 hover:!bg-secondary-600 text-gray-0":
                        currentDate?.format(DATE_FORMAT) ===
                        selectedDate?.format(DATE_FORMAT),
                    })}
                  >
                    {currentDate.date()}
                  </td>
                );
              }}
              className={cn("h-full w-full px-4")}
              inputProps={{
                readOnly: true,
                className: cn(
                  "h-full w-full bg-gray-0 font-medium border-0 focus:outline-none cursor-pointer text-gray-600 placeholder:text-gray-600",
                ),
              }}
            />
          </div>
        </div>
        <NotPrimaryMembershipWarning membership={membership} />
      </div>
    </ConfirmModal>
  );
};
